import React, { FC, useEffect, useState } from "react";
import {
  Form, Button, message, Switch, DatePicker,
  Input, Spin, Table, Tag, Empty, Upload, Select,
} from "antd";
import { UploadOutlined, PlusOutlined, ArrowLeftOutlined, EditOutlined, ShopOutlined } from "@ant-design/icons";
import { BASE_URL } from "../../../constants/urlConstants";
import dayjs from "dayjs";
import "./AddDistributor.css";

const { TextArea } = Input;

// Basic UPI ID validation: name@bank (e.g. distributor@upi, 9876543210@ybl)
const UPI_REGEX = /^[\w.\-]{2,49}@[a-zA-Z]{2,64}$/;

const AddDistributor: FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading]               = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [data, setData]                     = useState<any[]>([]);
  const [showForm, setShowForm]             = useState(false);
  const [editingId, setEditingId]           = useState<number | null>(null);

  /* ── FETCH ── */
  const fetchDistributors = async () => {
    try {
      setInitialLoading(true);
      const res = await fetch(`${BASE_URL}/api/finalle/dist/all`);
      if (!res.ok) throw new Error("Failed to fetch");
      setData(await res.json());
    } catch (err: any) { message.error(err.message); }
    finally { setInitialLoading(false); }
  };

  useEffect(() => { fetchDistributors(); }, []);

  /* ── SUBMIT ── */
  const onFinish = async (values: any) => {
    try {
      if (values.parentType === "ME" && !values.meEmpId) { message.error("ME Employee ID required"); return; }
      if (values.todayShopStatus === false && (!values.shopCloseReason || !values.shopCloseUntil)) {
        message.error("Pause reason & date required"); return;
      }
      setLoading(true);
      const fd = new FormData();
      fd.append("businessName", values.businessName);
      fd.append("address", values.address);
      fd.append("mobile", values.mobile);
      fd.append("email", values.email);
      fd.append("pincode", values.pincode);
      fd.append("upiId", values.upiId);
      fd.append("parentType", values.parentType);
      if (values.parentType === "ME") fd.append("meEmpId", values.meEmpId);
      fd.append("serviceStatus", String(values.serviceStatus ?? true));
      fd.append("todayShopStatus", String(values.todayShopStatus ?? true));
      if (values.todayShopStatus === false) {
        fd.append("shopCloseReason", values.shopCloseReason);
        fd.append("shopCloseUntil", dayjs(values.shopCloseUntil).format("YYYY-MM-DDT23:59:59"));
      }
      if (values.distributionEndDate)
        fd.append("distributionEndDate", dayjs(values.distributionEndDate).format("YYYY-MM-DDT23:59:59"));
      values.attachments?.forEach((f: any) => { if (f.originFileObj) fd.append("attachments", f.originFileObj); });

      const isEdit = !!editingId;
      const url = isEdit
        ? `${BASE_URL}/api/finalle/updateDistributor/${editingId}`
        : `${BASE_URL}/api/finalle/addDistributor`;
      const res = await fetch(url, { method: "POST", body: fd });
      if (!res.ok) throw new Error(isEdit ? "Update failed" : "Create failed");
      message.success(isEdit ? "Distributor updated!" : "Distributor added!");
      form.resetFields(); setShowForm(false); setEditingId(null); fetchDistributors();
    } catch (err: any) { message.error(err.message); }
    finally { setLoading(false); }
  };

  /* ── EDIT ── */
  const handleEdit = (record: any) => {
    setEditingId(record.id); setShowForm(true);
    const existingFiles = record.attachmentPaths?.map((url: string, i: number) => ({
      uid: `existing-${i}`, name: url.split("/").pop(), status: "done", url,
    })) || [];
    form.setFieldsValue({
      ...record,
      shopCloseUntil:      record.shopCloseUntil      ? dayjs(record.shopCloseUntil)      : null,
      distributionEndDate: record.distributionEndDate ? dayjs(record.distributionEndDate) : null,
      attachments: existingFiles,
    });
  };

  const handleCancel = () => { setShowForm(false); setEditingId(null); form.resetFields(); };

  /* ── TABLE COLUMNS (desktop) ── */
  const columns = [
    { title: "ID",       dataIndex: "distributorId", width: 90 },
    { title: "Business", dataIndex: "businessName"  },
    { title: "Mobile",   dataIndex: "mobile", width: 120 },
    { title: "Pincode",  dataIndex: "pincode", width: 90 },
    { title: "UPI ID",   dataIndex: "upiId",  width: 160, render: (v: string) => v || "—" },
    {
      title: "Parent", width: 110,
      render: (r: any) => (
        <Tag color="blue" style={{ borderRadius: 2, fontWeight: 600, fontSize: 11 }}>
          {r.parentType}{r.meEmpId ? ` · ${r.meEmpId}` : ""}
        </Tag>
      ),
    },
    {
      title: "Service", width: 90,
      render: (r: any) => r.serviceStatus
        ? <Tag color="success" style={{ borderRadius: 2, fontWeight: 600, fontSize: 11 }}>Active</Tag>
        : <Tag color="error"   style={{ borderRadius: 2, fontWeight: 600, fontSize: 11 }}>Off</Tag>,
    },
    {
      title: "Shop", width: 90,
      render: (r: any) => r.todayShopStatus
        ? <Tag color="success" style={{ borderRadius: 2, fontWeight: 600, fontSize: 11 }}>Open</Tag>
        : <Tag color="error"   style={{ borderRadius: 2, fontWeight: 600, fontSize: 11 }}>Closed</Tag>,
    },
    {
      title: "Action", width: 80,
      render: (r: any) => (
        <button className="ad-edit-btn" onClick={() => handleEdit(r)}>
          <EditOutlined /> Edit
        </button>
      ),
    },
  ];

  if (initialLoading) return (
    <div className="ad-loader"><Spin size="large" /><p>Loading distributors…</p></div>
  );

  return (
    <div className="ad-page">

      {/* ── Top bar ── */}
      <div className="ad-topbar">
        <div className="ad-topbar-left">
          <ShopOutlined className="ad-topbar-icon" />
          <span className="ad-topbar-title">Manage Distributors</span>
          {data.length > 0 && <span className="ad-topbar-badge">{data.length} total</span>}
        </div>
        {!showForm && (
          <button className="ad-add-btn" onClick={() => setShowForm(true)}>
            <PlusOutlined /> Add
          </button>
        )}
      </div>

      {/* ── Breadcrumb ── */}
      <div className="ad-breadcrumb">
        Dashboard &rsaquo; Distributors{showForm && <> &rsaquo; {editingId ? "Edit" : "Add New"}</>}
      </div>

      {/* ══ FORM VIEW ══ */}
      {showForm ? (
        <div className="ad-form-body">
          <button className="ad-back-btn" onClick={handleCancel}>
            <ArrowLeftOutlined /> Back
          </button>

          <div className="ad-form-card">
            <div className="ad-form-card-header">
              <ShopOutlined />
              <span>{editingId ? "Edit Distributor" : "New Distributor"}</span>
              {editingId && <span className="ad-form-id-badge">#{editingId}</span>}
            </div>

            <div className="ad-form-card-body">
              <Form form={form} layout="vertical" onFinish={onFinish}
                initialValues={{ serviceStatus: true, todayShopStatus: true }}>

                <div className="ad-section-label">Basic Information</div>

                <div className="ad-form-row">
                  <Form.Item name="businessName" label="Business Name" rules={[{ required: true, message: "Required" }]}>
                    <Input size="large" placeholder="Business name" />
                  </Form.Item>
                  <Form.Item name="mobile" label="Mobile" rules={[{ required: true }, { pattern: /^\d{10}$/, message: "10 digits" }]}>
                    <Input size="large" maxLength={10} placeholder="10-digit number" />
                  </Form.Item>
                </div>

                <Form.Item name="address" label="Address" rules={[{ required: true, message: "Required" }]}>
                  <TextArea rows={3} placeholder="Full address" />
                </Form.Item>

                <div className="ad-form-row">
                  <Form.Item name="email" label="Email" rules={[{ required: true }, { type: "email", message: "Valid email" }]}>
                    <Input size="large" placeholder="email@example.com" />
                  </Form.Item>
                  <Form.Item name="pincode" label="Pincode" rules={[{ required: true }, { len: 6, message: "6 digits" }]}>
                    <Input size="large" maxLength={6} placeholder="6-digit pincode" />
                  </Form.Item>
                </div>

                <Form.Item
                  name="upiId"
                  label="UPI ID"
                  rules={[
                    { required: true, message: "Required" },
                    { pattern: UPI_REGEX, message: "Enter a valid UPI ID (e.g. name@bank)" },
                  ]}
                >
                  <Input size="large" placeholder="distributor@upi" />
                </Form.Item>

                <div className="ad-section-label">Parent & Assignment</div>

                <div className="ad-form-row">
                  <Form.Item name="parentType" label="Parent Type" rules={[{ required: true, message: "Required" }]}>
                    <Select size="large" placeholder="Select type"
                      options={[{ label: "HQ — Head Office", value: "HQ" }, { label: "ME — Marketing Exec", value: "ME" }]} />
                  </Form.Item>
                  <Form.Item shouldUpdate noStyle>
                    {({ getFieldValue }) => getFieldValue("parentType") === "ME" ? (
                      <Form.Item name="meEmpId" label="ME Employee ID" rules={[{ required: true, message: "Required" }]}>
                        <Input size="large" placeholder="Employee ID" />
                      </Form.Item>
                    ) : <div />}
                  </Form.Item>
                </div>

                <Form.Item name="distributionEndDate" label="Distribution End Date">
                  <DatePicker size="large" style={{ width: "100%" }} />
                </Form.Item>

                <div className="ad-section-label">Status</div>

                <div className="ad-switch-row">
                  <div className="ad-switch-item">
                    <Form.Item name="serviceStatus" valuePropName="checked" noStyle>
                      <Switch />
                    </Form.Item>
                    <div>
                      <p className="ad-switch-label">Service Status</p>
                      <p className="ad-switch-hint">Enable or disable this distributor</p>
                    </div>
                  </div>
                  <div className="ad-switch-item">
                    <Form.Item name="todayShopStatus" valuePropName="checked" noStyle>
                      <Switch />
                    </Form.Item>
                    <div>
                      <p className="ad-switch-label">Today's Shop Status</p>
                      <p className="ad-switch-hint">Mark shop as open or temporarily closed</p>
                    </div>
                  </div>
                </div>

                <Form.Item shouldUpdate noStyle>
                  {({ getFieldValue }) => getFieldValue("todayShopStatus") === false ? (
                    <div className="ad-closed-box">
                      <p className="ad-closed-box-title">⚠️ Shop Closed — Details Required</p>
                      <Form.Item name="shopCloseReason" label="Close Reason" rules={[{ required: true, message: "Required" }]}>
                        <TextArea rows={2} placeholder="Reason for closing" />
                      </Form.Item>
                      <Form.Item name="shopCloseUntil" label="Closed Until" rules={[{ required: true, message: "Required" }]}>
                        <DatePicker size="large" style={{ width: "100%" }} />
                      </Form.Item>
                    </div>
                  ) : null}
                </Form.Item>

                <div className="ad-section-label">Attachments</div>

                <Form.Item name="attachments" valuePropName="fileList" getValueFromEvent={(e) => e?.fileList}>
                  <Upload beforeUpload={() => false} multiple>
                    <button type="button" className="ad-upload-btn">
                      <UploadOutlined /> Select Files
                    </button>
                  </Upload>
                </Form.Item>

                <div className="ad-form-actions">
                  <button type="submit" className={`ad-submit-btn${loading ? " loading" : ""}`} disabled={loading}>
                    {loading ? "Saving…" : editingId ? "Update Distributor" : "Add Distributor"}
                  </button>
                  <button type="button" className="ad-cancel-btn" onClick={handleCancel}>Cancel</button>
                </div>
              </Form>
            </div>
          </div>
        </div>

      ) : data.length === 0 ? (
        <div className="ad-empty">
          <Empty description={<span className="ad-empty-text">No distributors found.</span>} />
          <button className="ad-add-btn" style={{ marginTop: 20 }} onClick={() => setShowForm(true)}>
            <PlusOutlined /> Add Distributor
          </button>
        </div>

      ) : (
        <div className="ad-table-wrap">

          {/* ── Desktop table ── */}
          <div className="ad-table-desktop">
            <Table dataSource={data} columns={columns} rowKey="id"
              pagination={{ pageSize: 10, showSizeChanger: false }}
              className="ad-table" rowClassName={() => "ad-table-row"} scroll={{ x: true }} />
          </div>

          {/* ── Mobile card list ── */}
          <div className="ad-table-mobile">
            <div className="ad-card-list">
              {data.map((r: any) => (
                <div key={r.id} className="ad-dist-card">
                  <div className="ad-dist-card-header">
                    <span className="ad-dist-card-id">{r.distributorId}</span>
                    <span className="ad-dist-card-name">{r.businessName}</span>
                  </div>
                  <div className="ad-dist-card-body">
                    <div className="ad-dist-card-row">
                      <span className="ad-dist-card-row-label">Mobile</span>
                      <span>{r.mobile}</span>
                    </div>
                    <div className="ad-dist-card-row">
                      <span className="ad-dist-card-row-label">Pincode</span>
                      <span>{r.pincode}</span>
                    </div>
                    <div className="ad-dist-card-row">
                      <span className="ad-dist-card-row-label">UPI ID</span>
                      <span>{r.upiId || "—"}</span>
                    </div>
                    <div className="ad-dist-card-row">
                      <span className="ad-dist-card-row-label">Parent</span>
                      <Tag color="blue" style={{ borderRadius: 2, fontWeight: 600, fontSize: 11, margin: 0 }}>
                        {r.parentType}{r.meEmpId ? ` · ${r.meEmpId}` : ""}
                      </Tag>
                    </div>
                  </div>
                  <div className="ad-dist-card-footer">
                    <div className="ad-dist-card-tags">
                      {r.serviceStatus
                        ? <Tag color="success" style={{ borderRadius: 2, fontWeight: 600, fontSize: 11, margin: 0 }}>Active</Tag>
                        : <Tag color="error"   style={{ borderRadius: 2, fontWeight: 600, fontSize: 11, margin: 0 }}>Off</Tag>}
                      {r.todayShopStatus
                        ? <Tag color="success" style={{ borderRadius: 2, fontWeight: 600, fontSize: 11, margin: 0 }}>Open</Tag>
                        : <Tag color="error"   style={{ borderRadius: 2, fontWeight: 600, fontSize: 11, margin: 0 }}>Closed</Tag>}
                    </div>
                    <button className="ad-edit-btn" onClick={() => handleEdit(r)}>
                      <EditOutlined /> Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default AddDistributor;